/*
 * @Descripttion: 场景管理 Translator
 * @Date: 2024-08-06 10:12:42
 */
import { fen2yuan, yuan2fen } from '@/utils/translator';
import type {
  SceneManageProductItem,
  SceneManageSceneDisplayItem,
  SceneManageSaveParams
} from './types';

/**
 * SceneManage 展示数据 Translator（纯接口 V1 数据流）
 * 入参：全量 products（SceneManageProductItem[]，含 selected 标记）
 * 处理：
 *   1. 仅保留 selected=true 的产品及其 selected=true 的场景（详情只展示勾选数据）
 *   2. 场景按 sceneId 合并为唯一行，同场景多产品放入 products 子集
 *   3. 金额分→元（productBudget / budget / 三档价格 / 累计结算）
 * 输出：_scenes（SceneManageSceneDisplayItem[]，详情表格用）
 * 提交时直接使用原 products 全量（selected 标记），不做反向转换
 */
export const sceneManageToDisplayTranslator = (
  products: SceneManageProductItem[]
): SceneManageSceneDisplayItem[] => {
  const mergedMap = new Map<number, SceneManageSceneDisplayItem>();

  // detail 接口只返回勾选数据（无 selected 字段）、弹窗 emit 的勾选集合 scenes 只含勾选场景——无需 selected 过滤
  (products || []).forEach(product => {
    (product.scenes || []).forEach(scene => {
      let row = mergedMap.get(scene.sceneId);
      if (!row) {
        row = {
          ...scene,
          products: []
        };
        mergedMap.set(scene.sceneId, row);
      }
      row.products.push({
        productId: String(product.productId || ''),
        productName: product.productName || '',
        productCategory: product.productCategory || '',
        productAttribute: product.productAttribute || '',
        lifecycleStage: product.lifecycleStage || ''
      });
    });
  });

  return Array.from(mergedMap.values());
};

/** 场景价格字段分→元（translators 层统一转换，页面层直接用元；effectivePrice 系列不用） */
const scenePriceFen2Yuan = (scene: any) => {
  if (!scene) return;
  const fields = [
    'platformPriceLower',
    'platformPriceUpper',
    'industryPriceLower',
    'industryPriceUpper',
    'budget',
    'cumulativeSettlementAmount'
  ];
  fields.forEach(f => {
    if (scene[f] != null) {
      scene[f] = fen2yuan(scene[f]);
    }
  });
};

/** detail 响应转换（apis 层调用）：products[].scenes 金额分→元（返回原 resp 引用） */
export const sceneManageDetailToYuan = (resp: any) => {
  (resp?.data?.products || []).forEach((product: any) => {
    (product.scenes || []).forEach(scenePriceFen2Yuan);
  });
  return resp;
};

/** 场景分页响应转换（apis 层调用）：scenePage.list 金额分→元（返回原 resp 引用） */
export const scenePageToYuan = (resp: any) => {
  (resp?.data?.scenePage?.list || []).forEach(scenePriceFen2Yuan);
  return resp;
};

/**
 * 保存参数构造：页面元数据 → 精简提交体（只提取文档字段，丢弃 itemId/selected/快照）+ 元→分
 * V2 文档：产品=productId/productName?/productBudget?/scenes[]；场景=sceneId/industryPriceLower+Upper(成对)/budget?
 */
export const buildSceneManageSaveParams = (
  params: SceneManageSaveParams
): SceneManageSaveParams => ({
  planId: params.planId || undefined,
  planScope: params.planScope,
  industryId: params.industryId,
  aspId: params.aspId,
  createType: params.createType,
  totalBudget: params.totalBudget,
  lastUpdateDt: params.lastUpdateDt,
  products: (params.products || []).map(product => ({
    productId: product.productId,
    productName: product.productName,
    productBudget:
      product.productBudget != null ? yuan2fen(product.productBudget) : null,
    scenes: (product.scenes || []).map(scene => ({
      sceneId: scene.sceneId,
      industryPriceLower:
        scene.industryPriceLower != null
          ? yuan2fen(scene.industryPriceLower)
          : null,
      industryPriceUpper:
        scene.industryPriceUpper != null
          ? yuan2fen(scene.industryPriceUpper)
          : null,
      budget: scene.budget != null ? yuan2fen(scene.budget) : null
    }))
  }))
});
